import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, signal, effect, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable, retry } from 'rxjs';
import { AuthService } from './auth.service';

/** Shape of GET /user/access — the signed-in user's plan + entitlement. */
export interface UserAccess {
  plan: string;
  status: string;
  has_access: boolean;
  end_date: string | null;
  default_level: string | null;
  features: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  apiUrl: string = environment.apiUrl

  /** The current user's access, cached once loaded. Null until it resolves. */
  readonly userAccess = signal<UserAccess | null>(null);
  private accessLoaded = false;

  constructor(private http: HttpClient) {
    const auth = inject(AuthService);
    // Drive access loading off the authenticated user, not component timing.
    // Fetching in a gate's ngOnInit could run before the session is established
    // right after login (first call came back unauthenticated → page didn't
    // blur until a manual refresh). Keying off currentUser means we fetch as
    // soon as the user is known, and re-fetch when a different user signs in.
    effect(() => {
      const user = auth.currentUser();
      if (user) {
        this.loadUserAccess();
      } else {
        // Signed out / not yet authenticated: drop cached access so the next
        // signed-in user is re-evaluated and nothing stays blurred post-logout.
        this.accessLoaded = false;
        this.userAccess.set(null);
      }
    }, { allowSignalWrites: true });
  }

  /** GET /user/access — the user's plan, status and per-page entitlement. */
  getUserAccess(): Observable<UserAccess> {
    return this.http.get<UserAccess>(`${this.apiUrl}/user/access`);
  }

  /**
   * True when the content should be soft-gated (blurred): the user is a free
   * trial user (plan "Free" + status "trial"). Null access (loading / failed) is
   * treated as not locked, so nothing flashes blurred before access resolves.
   * Reads the `userAccess` signal, so callers can use it inside a computed() to
   * stay reactive.
   */
  isPlanLocked(): boolean {
    const a = this.userAccess();
    if (!a) return false;
    return a.plan?.toLowerCase() === 'free' && a.status?.toLowerCase() === 'trial';
  }

  /**
   * Fetch the user's access once and cache it in the `userAccess` signal.
   * Idempotent — only the first call per signed-in user hits the API. Retries a
   * couple of times so a transient failure right after login doesn't leave the
   * page un-gated.
   */
  loadUserAccess(): void {
    if (this.accessLoaded) return;
    this.accessLoaded = true;
    this.getUserAccess().pipe(retry({ count: 2, delay: 800 })).subscribe({
      next: res => this.userAccess.set(res ?? null),
      // Leave it null (unblurred) and allow a later retry rather than locking the
      // page out on a transient failure.
      error: () => { this.userAccess.set(null); this.accessLoaded = false; },
    });
  }

  getSubscription(): Observable<any[]>{
    return this.http.get<any>(`${this.apiUrl}/subscription/plan-details`);
  }

  getPageSubscription(): Observable<any[]>{
    return this.http.get<any>(`${this.apiUrl}/subscription/alacarte-details`);
  }

  getPlan(): Observable<any[]>{
    return this.http.get<any>(`${this.apiUrl}/subscription/plan`);
  }

  getPlans(): Observable<any[]>{
    return this.http.get<any>(`${this.apiUrl}/subscription/plan-list`);
  }

  getPages(): Observable<any[]>{
    return this.http.get<any>(`${this.apiUrl}/subscription/pages`);
  }

  getPaymentLink(formData: { plan_id:number|undefined | null,days:number | undefined,pages_id:number[] | null | undefined, apply_prorate?:boolean, coupon_code?:string }): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(`${this.apiUrl}/payment/generate-url`, formData, { headers: headers });
  }

  getPurchasePreview(plan_id: number | undefined | null, pages_id: number[] | undefined | null, days: number, coupon_code?: string): Observable<any> {
    let url = `${this.apiUrl}/payment/purchase-preview?days=${days}`;
    if (plan_id) url += `&plan_id=${plan_id}`;
    if (pages_id?.length) url += pages_id.map(id => `&pages_id=${id}`).join('');
    if (coupon_code) url += `&coupon_code=${encodeURIComponent(coupon_code)}`;
    return this.http.get<any>(url);
  }

  validateCoupon(code: string, plan_id?: number | null, pages_id?: number[] | null, base_amount?: number): Observable<any> {
    let url = `${this.apiUrl}/payment/coupon/validate?code=${encodeURIComponent(code)}&base_amount=${base_amount ?? 0}`;
    if (plan_id) url += `&plan_id=${plan_id}`;
    if (pages_id?.length) url += pages_id.map(id => `&pages_id=${id}`).join('');
    return this.http.get<any>(url);
  }

  hasAccessToPage(page_id:number): Observable<any[]>{
    return this.http.get<any>(`${this.apiUrl}/user/check-page-permission?page_id=${page_id}`);
  }

  getMySubscription(): Observable<any[]>{
    return this.http.get<any>(`${this.apiUrl}/user/subscriptions-details`);
  }

  getAlacarteEligibility(): Observable<{ eligible: boolean }>{
    return this.http.get<{ eligible: boolean }>(`${this.apiUrl}/user/alacarte-eligibility`);
  }


}
